import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, tagApi } from '../utils/api';
import ChessDiagram from '../components/ChessDiagram';

export default function QuizModePage() {
  const { token, prefs } = useAuth();
  // eslint-disable-next-line
  const navigate  = useNavigate();

  // ── State ─────────────────────────────────────────────────────────────────
  const [allCards, setAllCards]     = useState([]);
  const [deck, setDeck]             = useState([]);        // shuffled working deck
  const [idx, setIdx]               = useState(0);         // current position
  const [showSolution, setShowSolution] = useState(false);
  const [started, setStarted]       = useState(false);
  const [done, setDone]             = useState(false);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(true);
  // Map of flashcardId → tag name array, loaded once
  const [tagsByCard, setTagsByCard] = useState({});

  useEffect(() => {
    Promise.all([
      flashcardApi.list(token),
      tagApi.listAllForUser(token).catch(() => []),
    ]).then(([cards, tagInfos]) => {
      setAllCards(cards || []);
      // Build cardId → string[] map
      const map = {};
      for (const info of (tagInfos || [])) {
        if (!map[info.flashcard_id]) map[info.flashcard_id] = [];
        map[info.flashcard_id].push(info.tag);
      }
      setTagsByCard(map);
    }).catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  // ── Controls ──────────────────────────────────────────────────────────────

  const shuffle = arr => [...arr].sort(() => Math.random() - 0.5);

  const startQuiz = useCallback(() => {
    setDeck(shuffle(allCards));
    setIdx(0);
    setShowSolution(false);
    setDone(false);
    setStarted(true);
  }, [allCards]);

  const next = () => {
    setShowSolution(false);
    if (idx + 1 >= deck.length) {
      setDone(true);
    } else {
      setIdx(i => i + 1);
    }
  };

  const cancel = () => {
    setStarted(false);
    setDone(false);
    setIdx(0);
    setShowSolution(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;
  if (error)   return <div className="alert alert-danger">{error}</div>;

  if (allCards.length === 0) return (
    <div className="text-center mt-5">
      <p className="text-muted">You have no flashcards. <a href="/flashcards/new">Create one first.</a></p>
    </div>
  );

  // ── Not started ────────────────────────────────────────────────────────────
  if (!started) return (
    <div className="row justify-content-center">
      <div className="col-md-5 text-center">
        <h2 className="mb-3">Quiz Mode</h2>
        <p className="text-muted mb-4">
          {allCards.length} card{allCards.length !== 1 ? 's' : ''} in your deck.
          Cards will be shown in random order.
        </p>
        <button className="btn btn-primary btn-lg" onClick={startQuiz}>Start Quiz</button>
      </div>
    </div>
  );

  // ── Done ───────────────────────────────────────────────────────────────────
  if (done) return (
    <div className="row justify-content-center">
      <div className="col-md-5 text-center">
        <h2 className="mb-3">Quiz Complete!</h2>
        <p className="text-muted mb-4">You reviewed all {deck.length} cards.</p>
        <div className="d-flex gap-3 justify-content-center">
          <button className="btn btn-primary" onClick={startQuiz}>Shuffle &amp; Restart</button>
          <button className="btn btn-outline-secondary" onClick={cancel}>Back</button>
        </div>
      </div>
    </div>
  );

  // ── In progress ────────────────────────────────────────────────────────────
  const card = deck[idx];
  const progress = Math.round(((idx + 1) / deck.length) * 100);
  // Card-level flipped overrides the user's diagram preference
  const cardPrefs  = { ...prefs, diagramFlipped: card.flipped ?? prefs.diagramFlipped };
  const showTags   = prefs.showTagsInQuiz === true;
  const currentTags = (tagsByCard[card.id] || []);

  return (
    <div className="row justify-content-center">
      <div className="col-lg-7">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted small">Card {idx + 1} of {deck.length}</span>
          <button className="btn btn-outline-secondary btn-sm" onClick={cancel}>Cancel Quiz</button>
        </div>

        {/* Progress bar */}
        <div className="progress mb-3" style={{ height: 6 }}>
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>

        {/* Card */}
        <div className="card shadow">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{card.name}</h4>
            {card.to_move && (
              <span className={`badge ${card.to_move === 'W' ? 'bg-light text-dark border' : 'bg-dark'}`}>
                {card.to_move === 'W' ? 'White to move' : 'Black to move'}
              </span>
            )}
          </div>
          <div className="card-body">
            {card.fen && (
              <div className="text-center mb-3">
                <ChessDiagram fen={card.fen} prefs={cardPrefs} size={320} />
              </div>
            )}
            {/* ── Tags ──────────────────────────────────────────── */}
            {showTags && currentTags.length > 0 && (
              <div className="mb-3 d-flex flex-wrap gap-1">
                {currentTags.map(tag => (
                  <span key={tag} className="badge rounded-pill bg-secondary">{tag}</span>
                ))}
              </div>
            )}

            {card.problem && (
              <div className="mb-4">
                <h6 className="text-muted text-uppercase small mb-2">Problem</h6>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{card.problem}</p>
              </div>
            )}

            {/* Solution toggle */}
            <div className="mb-2">
              <button
                className={`btn ${showSolution ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => setShowSolution(s => !s)}
              >
                {showSolution ? 'Hide Solution' : 'Show Solution'}
              </button>
            </div>
            {showSolution && (
              <div className="solution-box mt-2">
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {card.solution || <em className="text-muted">No solution provided.</em>}
                </p>
              </div>
            )}
          </div>
          <div className="card-footer bg-transparent text-end">
            <button className="btn btn-primary" onClick={next}>
              {idx + 1 < deck.length ? 'Next Card →' : 'Finish Quiz'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
